import { FormEvent, useRef, useState } from "react";
import {
  ContactMessage,
  contactErrorMessage,
  sendMessage,
  validateMessage,
} from "../services/contact";
import { site } from "../data/site";

type SubmissionState = "idle" | "sending" | "success" | "error";
export function useContactForm() {
  const form = useRef<HTMLFormElement>(null);
  const inFlight = useRef(false);
  const sent = useRef(0);
  const [state, setState] = useState<SubmissionState>("idle");
  const [feedback, setFeedback] = useState("");
  const [draft, setDraft] = useState<ContactMessage>({
    user_name: "",
    user_email: "",
    message: "",
  });
  const composeHref = `mailto:${site.email}?subject=${encodeURIComponent(`Portfolio enquiry from ${draft.user_name || "a visitor"}`)}&body=${encodeURIComponent(`${draft.message}\n\nFrom: ${draft.user_name}\nReply to: ${draft.user_email}`)}`;

  function updateDraft() {
    if (!form.current) return;
    const values = new FormData(form.current);
    setDraft({
      user_name: String(values.get("user_name") || ""),
      user_email: String(values.get("user_email") || ""),
      message: String(values.get("message") || ""),
    });
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.current || inFlight.current) return;
    const data = new FormData(form.current);
    if (data.get("website")) return;
    const message: ContactMessage = {
      user_name: String(data.get("user_name") || "").trim(),
      user_email: String(data.get("user_email") || "").trim(),
      message: String(data.get("message") || "").trim(),
    };
    setDraft(message);
    const invalid = validateMessage(message);
    if (invalid || Date.now() - sent.current < 60000) {
      setState("error");
      setFeedback(
        invalid || "Please wait a minute before sending another message.",
      );
      return;
    }
    inFlight.current = true;
    setState("sending");
    setFeedback("Sending your message…");
    try {
      await sendMessage(message);
      sent.current = Date.now();
      setState("success");
      setFeedback(
        "Thanks for reaching out. Your message has been submitted successfully.",
      );
      form.current?.reset();
    } catch (error) {
      // Keep provider diagnostics in the developer console, not visitor-facing copy.
      console.error("Contact submission failed", error);
      setState("error");
      setFeedback(contactErrorMessage(error));
    } finally {
      inFlight.current = false;
    }
  }
  return { form, state, feedback, submit, composeHref, updateDraft };
}
